import { faChevronLeft, faChevronRight, faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';

import Button from '../Button';

import * as style from './style.module.css';

const range = (from, to) => new Array(to - from + 1).fill('').map((_, i) => from + i);

const PaginationButton = ({ content = '', className = '', icon = null, hideOnMobile, active, disabled, action = () => {} }) => (
  <Button className={`${style.block} ${hideOnMobile ? style.hideOnMobile : ''} ${className || ''}`} active={active} disabled={disabled} action={action} label={content} icon={icon} theme="SECONDARY" />
);

const PaginationPreviousButton = ({ ...props }) => <PaginationButton {...props} icon={faChevronLeft} />;

const PaginationNextButton = ({ ...props }) => <PaginationButton {...props} icon={faChevronRight} />;

const PaginationDotsButton = ({ ...props }) => <PaginationButton {...props} disabled icon={faEllipsis} hideOnMobile />;

const ClassicPagination = ({ totalPages = 0, currentPage = 0, disabled = false, onPageChange = () => {} }) => {
  if (totalPages <= 1) {
    return null;
  }

  const currentIndex = currentPage - 2;
  const pages = range(2, totalPages - 1);

  const handlePage = (page) => (page >= 1 && page <= totalPages ? () => onPageChange(page) : () => {});

  return (
    <div className={style.container}>
      <div className={style.pagination}>
        {/* Previous page */}
        {currentPage > 1 && <PaginationPreviousButton disabled={disabled} action={handlePage(currentPage - 1)} />}

        {/* First page */}
        {currentPage !== 1 && <PaginationButton content={1} disabled={disabled} action={handlePage(1)} />}

        {/* Spacing */}
        {pages[currentIndex - 3] && <PaginationDotsButton />}

        {/* 2 pages before the current page */}
        <PaginationButton content={pages[currentIndex - 2]} disabled={disabled} action={handlePage(pages[currentIndex - 2])} hideOnMobile />
        <PaginationButton content={pages[currentIndex - 1]} disabled={disabled} action={handlePage(pages[currentIndex - 1])} hideOnMobile />

        {/* Active page */}
        <PaginationButton content={currentPage} disabled={disabled} action={handlePage(currentPage)} active />

        {/* 2 pages after the current page */}
        <PaginationButton content={pages[currentIndex + 1]} disabled={disabled} action={handlePage(pages[currentIndex + 1])} hideOnMobile />
        <PaginationButton content={pages[currentIndex + 2]} disabled={disabled} action={handlePage(pages[currentIndex + 2])} hideOnMobile />

        {/* Spacing */}
        {pages[currentIndex + 3] && <PaginationDotsButton />}

        {/* Last page */}
        {currentPage !== totalPages && <PaginationButton content={totalPages} disabled={disabled} action={handlePage(totalPages)} />}

        {/* Next page */}
        {currentPage < totalPages && <PaginationNextButton disabled={disabled} action={handlePage(currentPage + 1)} />}
      </div>
    </div>
  );
};

ClassicPagination.propTypes = {
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  currentPage: PropTypes.number,
};

const RelayPagination = ({ first, data, disabled = false, onClick = () => {} }) => {
  const pageInfo = data?.pageInfo;
  const pageCursors = data?.pageCursors;
  const totalPages = data?.totalPages;

  if (!totalPages || totalPages <= 1 || !pageInfo || !pageCursors?.around) {
    return null;
  }

  const hasFirstPage = pageCursors.around.some((e) => e.pageNumber === 1);
  const hasLastPage = pageCursors.around.some((e) => e.pageNumber === totalPages);

  const onNext = () => {
    if (pageInfo.hasNextPage) {
      onClick({ first, after: pageCursors?.next?.cursor || null, last: null, before: null });
    }
  };

  const onPrev = () => {
    if (pageInfo.hasPreviousPage) {
      onClick({ first, after: pageCursors?.previous?.cursor || null, last: null, before: null });
    }
  };

  const onPage = (pageCursor) => () => {
    onClick({ first, after: pageCursor?.cursor, last: null, before: null });
  };

  return (
    <div className={style.container}>
      <div className={style.pagination}>
        {/* Previous page */}
        {pageInfo.hasPreviousPage && <PaginationPreviousButton disabled={disabled} action={onPrev} />}

        {/* First page */}
        {!hasFirstPage && (
          <>
            <PaginationButton content={1} disabled={disabled} action={onPage(pageCursors.first)} />
            <PaginationDotsButton className={style.hideOnMobile} />
          </>
        )}

        {/* Around pages */}
        {pageCursors.around.map((around) => (
          <PaginationButton
            className={style.hideOnMobile}
            key={around.pageNumber}
            content={around.pageNumber}
            disabled={disabled}
            action={around.isCurrent ? null : onPage(around)}
            active={around.isCurrent}
            hideOnMobile={!around.isCurrent}
          />
        ))}
        {pageCursors.around
          .filter((e) => e.isCurrent)
          .map((around) => (
            <PaginationButton
              className={style.hideOnDesktop}
              key={around.pageNumber}
              content={around.pageNumber}
              disabled={disabled}
              action={around.isCurrent ? null : onPage(around)}
              active={around.isCurrent}
              hideOnMobile={!around.isCurrent}
            />
          ))}

        {/* Last page */}
        {!hasLastPage && (
          <>
            <PaginationDotsButton className={style.hideOnMobile} />
            <PaginationButton content={totalPages} disabled={disabled} action={onPage(pageCursors.last)} />
          </>
        )}

        {/* Next page */}
        {pageInfo.hasNextPage && <PaginationNextButton disabled={disabled} action={onNext} />}
      </div>
    </div>
  );
};

RelayPagination.propTypes = {
  first: PropTypes.number,
  data: PropTypes.object,
  onClick: PropTypes.func,
};

export { ClassicPagination, RelayPagination };
